// chat-entry.js — 哈密聊天入口（主站外壳）
// 只负责全站浮窗外壳相关：registerPanel 注册、拖拽归档、未读红点轮询、跳主站面板、拖入校验。
// 聊天正文（消息渲染/发送/历史/卡片/进度轮询/状态气泡）已抽到 chat-core.js，与设计器共用同一份，
// 避免两套漂移。本模块通过 chatCore.setChatDeps 注入主站的真实外壳依赖。

import toast from '@lib/toast.js';
import { API_BASE } from '@lib/config.js';
import { registerPanel, openPanel, isOpen, closePanel } from '@lib/panel-manager.js';
import { renderAvatarInto, openProfilePanel } from '@lib/profile-panel.js';
import { openHamiSettings } from '@lib/hami-settings.js';
import { openPersonDetail } from '@lib/person-detail.js';
import { openOrgDetail } from '@lib/org-detail.js';
import { openFilePicker } from './chat-file-picker.js';
import * as chatCore from './chat-core.js';

// ===== 注入主站外壳依赖（模块加载即注入；buildChatBody 运行时消费） =====
chatCore.setChatDeps({
  renderAvatarInto: renderAvatarInto,
  openProfilePanel: openProfilePanel,
  openHamiSettings: openHamiSettings,
  openPersonDetail: openPersonDetail,
  openOrgDetail: openOrgDetail,
  navigateProject: function(id) {
    if (window.__spaNavigate) window.__spaNavigate('/project/' + encodeURIComponent(id));
  },
  closeChatPanel: function() { closePanel({ type: 'chat', immediate: true }); },
  syncEntryIndicator: syncEntryIndicator
});

// 聊天面板头部右侧「…」按钮（三点横排），点击直接打开哈密设置
function buildMoreButton() {
  var b = document.createElement('button');
  b.type = 'button';
  b.className = 'panel-more';
  b.setAttribute('aria-label', '哈密设置');
  b.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>';
  b.addEventListener('click', function() { openHamiSettings(); });
  return b;
}

// ===== 哈密入口徽标 + 照片处理完成主动提示（阶段 3 启用：轮询拉取未读，原位刷新头像） =====
var _unreadPersonIds = [];

// 入口徽标统一状态：后台处理中（理解中/处理中）→ 转圈环；处理完有未读 → 涟漪红点
function syncEntryIndicator() {
  var busy = chatCore.getStatusMode() === 'understanding' || chatCore.getStatusMode() === 'processing';
  var unread = _unreadPersonIds.length > 0;
  document.querySelectorAll('.nav-dot').forEach(function(d) {
    d.classList.toggle('busy', busy);
    d.classList.toggle('show', !busy && unread);
  });
}

// 原位刷新资料库/项目页里已渲染的头像（处理图覆盖后 URL 不变、浏览器缓存旧图，追加时间戳穿透缓存）
function refreshAvatars(personIds) {
  (personIds || []).forEach(function(id) {
    var sel = 'img[src*="/api/people/' + id + '/photo"]';
    document.querySelectorAll(sel).forEach(function(img) {
      var base = img.getAttribute('src').split('?')[0];
      img.setAttribute('src', base + '?v=' + Date.now());
    });
  });
}

// 查未读照片处理通知（驻留时轮询）：只亮红点 + 刷新头像，不渲染卡片（系统消息已在历史）
// 顺带查一次进度：照片后台任务在跑（对话任务由 runRound 自己轮询）→ 显示状态气泡 + 启动轮询
async function pollNotifications() {
  try {
    var res = await fetch(API_BASE + '/api/agent/notifications', { credentials: 'include' });
    if (!res.ok) return;
    var data = await res.json();
    var ids = data.unread || [];
    _unreadPersonIds = ids;
    syncEntryIndicator();
    if (ids.length > 0) {
      refreshAvatars(ids);
    }
  } catch (e) { /* 轮询失败忽略 */ }

  await chatCore.resumeProgress();
}

// 清未读（每次打开聊天面板时调用）
async function markNotificationsRead() {
  try {
    await fetch(API_BASE + '/api/agent/notifications/read', { method: 'POST', credentials: 'include' });
  } catch (e) { /* 忽略 */ }
  _unreadPersonIds = [];
  syncEntryIndicator();
}

// 驻留时每 20 秒轮询一次
setInterval(pollNotifications, 20000);
pollNotifications();

// ===== 面板开关 =====

function openChatPanel(projectId, files, command) {
  // 项目页拖入传 projectId；普通打开（无参）清空归属；command 为打开后自动发送的指令
  // 清一次全局高亮（项目卡 drop 拦住 document drop，这里补清残留）
  clearDropHighlight();

  // 拖入即校验：类型 + 大小，不合规剔除并 toast；全剔除则不开面板、也不改归属
  if (files && files.length) {
    files = validateFiles(files);
    if (!files.length) return;
  }

  // 校验通过后再记录归属（全不合规时不残留，也不会污染上一批已挂草稿气泡的归属）
  chatCore.setProjectId(projectId);
  chatCore.setPendingCommand(command);

  // 面板已打开 → 追加，不重建、不清空
  if (isOpen('chat')) {
    if (files && files.length) chatCore.addFiles(files);
    chatCore.sendPendingCommand();
    return;
  }

  openPanel('chat', { files: files || null });
}

// ===== 拖入校验：类型 + 大小，对齐后端 /api/upload 限制 =====
var ALLOWED_EXTS = ['.doc', '.docx', '.ppt', '.pptx', '.pdf', '.xlsx', '.jpg', '.jpeg', '.png', '.webp', '.gif'];
var MAX_FILE_SIZE = 20 * 1024 * 1024;

function validateFiles(files) {
  var ok = [];
  var bad = [];
  (files || []).forEach(function(f) {
    var name = (f.name || '').toLowerCase();
    var dot = name.lastIndexOf('.');
    var ext = dot >= 0 ? name.slice(dot) : '';
    var extOk = ALLOWED_EXTS.indexOf(ext) !== -1;
    var sizeOk = !f.size || f.size <= MAX_FILE_SIZE;
    if (extOk && sizeOk) {
      ok.push(f);
    } else {
      bad.push(f.name + '（' + (extOk ? '超过 20MB' : '类型不支持') + '）');
    }
  });
  if (bad.length) {
    toast('以下文件不支持，已跳过：' + bad.join('、'));
  }
  return ok;
}

// ===== 拖拽入口（需求 2）+ 落点高亮（步骤 4） =====
var _dragBound = false;

function dropZoneOf(el) {
  if (!el || !el.closest) return null;
  if (el.closest('[data-panel="chat"]')) return 'chat';
  var slot = document.getElementById('pageSlot');
  if (slot && slot.contains(el)) return 'page';
  return null;
}

var _dropTip = null;
function ensureDropTip() {
  if (_dropTip) return;
  _dropTip = document.createElement('div');
  _dropTip.className = 'drop-tip';
  _dropTip.textContent = '松手加入草稿气泡';
  document.body.appendChild(_dropTip);
}

function setDropHighlight(zone) {
  var slot = document.getElementById('pageSlot');
  var chatPanel = document.querySelector('.panel[data-panel="chat"]');
  if (slot) slot.classList.toggle('drop-zone', zone === 'page');
  if (chatPanel) chatPanel.classList.toggle('drop-zone', zone === 'chat');
  ensureDropTip();
  _dropTip.classList.toggle('show', !!zone);
}

function clearDropHighlight() {
  var slot = document.getElementById('pageSlot');
  var chatPanel = document.querySelector('.panel[data-panel="chat"]');
  if (slot) slot.classList.remove('drop-zone');
  if (chatPanel) chatPanel.classList.remove('drop-zone');
  if (_dropTip) _dropTip.classList.remove('show');
}

function hasFiles(e) {
  var dt = e.dataTransfer;
  if (!dt) return false;
  if (dt.types && Array.prototype.indexOf.call(dt.types, 'Files') !== -1) return true;
  return !!(dt.files && dt.files.length);
}

function bindDragEntry() {
  if (_dragBound) return;
  _dragBound = true;

  document.addEventListener('dragover', function(e) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDropHighlight(dropZoneOf(e.target));
  });

  document.addEventListener('drop', function(e) {
    clearDropHighlight();
    if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
    e.preventDefault();
    var zone = dropZoneOf(e.target);
    if (!zone) return;
    var list = Array.prototype.slice.call(e.dataTransfer.files);
    openWithProject(null, list);
  });

  document.addEventListener('dragleave', function(e) {
    if (e.relatedTarget === null) clearDropHighlight();
  });

  document.addEventListener('dragend', function() {
    clearDropHighlight();
  });
}

// ===== 面板注册（正文由 chat-core 提供，外壳/头部/遮罩/互斥由 manager 提供） =====

registerPanel('chat', {
  title: '哈密',
  subtitle: '在线',
  keepAlive: true, // 方案C：面板隐藏不销毁，历史/状态气泡/消息 DOM 常驻，解决服务器忙时重开丢历史 + 状态恢复
  headerRight: function() { return buildMoreButton(); }, // 头部右侧「…」按钮 → 打开哈密设置
  buildBody: function(container) { chatCore.buildChatBody(container); },
  // 打开：清未读 + 挂草稿 + 首次拉历史（_historyLoaded 守卫，keepAlive 重开不重复拉）+ 查一次进度
  onOpen: function(ctx) {
    // 打开面板即视作已读：清未读标记 + 灭红点（每次打开都清，不依赖首次载入历史）
    markNotificationsRead();
    chatCore.openChat(ctx);
  },
  // 关闭（keepAlive）：清归属/待发指令；面板 DOM、历史、状态气泡、轮询、播放全部保留，重开直接可见
  onClose: function() {
    chatCore.closeChat();
  }
});

// ===== 入口绑定 =====

var _chatBound = false;
export function bindChatEntry() {
  if (_chatBound) return;
  _chatBound = true;

  bindDragEntry();

  document.addEventListener('click', function(e) {
    var target = e.target;
    while (target && target !== document) {
      if (target.getAttribute && target.getAttribute('data-action') === 'chat') {
        openChatPanel();
        return;
      }
      target = target.parentNode;
    }
  });
}

// ===== 项目页拖文件归档入口（阶段 4）：打开聊天面板 + 预填 projectId + 文件挂草稿气泡 =====
function openWithProject(projectId, files) {
  openChatPanel(projectId, files);
}

// 打开聊天面板 + 预填 projectId + 自动发送指令（一键建库等）
function openWithCommand(projectId, command) {
  openChatPanel(projectId, null, command);
}

// 资料页「＋」：唤起系统文件选择器，选中文件后作为草稿加入聊天
function openUploadToChat() {
  openFilePicker(function(item) {
    openWithProject(null, [item.file]);
  });
}

// 暴露给页面 chunk 调用（跨 chunk 走 window 全局）
window.__hamiChat = window.__hamiChat || {};
window.__hamiChat.openWithProject = openWithProject;
window.__hamiChat.openWithCommand = openWithCommand;
window.__hamiChat.openUpload = openUploadToChat;
